import { TabPanel } from 'react-aria-components';
import { Route, Routes, useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { GenericError } from '@/components/GenericError/GenericError';
import Stack from '@/design_system/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList } from '@/design_system/Tabs/Tabs';
import { useShowToast } from '@/design_system/Toast';
import EditableTitle from '@/design_system/Typography/EditableTitle';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useCompany, useUpdateCompany } from '@/models/company';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';
import { NotFoundError } from '@/utils/error/NotFoundError';

import { EntitiesSection } from './sections/EntitiesSection';
import { OverviewSection } from './sections/OverviewSection';

export const Company = () => {
  const { '*': section, id } = useParams();
  const { t } = useLingui();
  const showToast = useShowToast();

  const {
    data: company,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error,
  } = useCompany(id);

  const { mutateAsync: updateCompany, isPending: isUpdateCompanyPending } = useUpdateCompany(id);

  if (isCompanyLoading) {
    return <Loading />;
  }

  if (isCompanyError || !company) {
    let errorMessage = <GenericError />;

    if (error instanceof NotFoundError) {
      errorMessage = (
        <Paragraph>
          <Trans id="settings.organization.invoicing.companies.detail.not-found">
            Unknown company or you do not have access to it.
          </Trans>
        </Paragraph>
      );
    }

    return <ErrorState errorMessage={errorMessage} />;
  }

  const handleSaveCompanyName = async (name: string) => {
    try {
      await updateCompany({ name });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.invoicing.companies.detail.rename.error">
            Could not rename the company. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <main>
      <Stack
        row
        className="settings-section__record-header"
        gap="0.5rem"
        alignItems="center"
        flexWrap="wrap"
      >
        <SettingsBreadcrumbParent
          to="/settings_beta/billing/companies"
          label={
            <Trans id="settings.organization.invoicing.companies.detail.back-to-companies">
              Companies
            </Trans>
          }
        />
        <EditableTitle
          value={company.name}
          onSave={handleSaveCompanyName}
          isSaving={isUpdateCompanyPending}
          titleProps={{ as: 'h1', size: '200', weight: 'medium' }}
          editAriaLabel={t({
            id: 'settings.organization.invoicing.companies.detail.editable-title.edit',
            message: 'Edit company name',
          })}
          saveAriaLabel={t({
            id: 'settings.organization.invoicing.companies.detail.editable-title.save',
            message: 'Save company name',
          })}
          cancelAriaLabel={t({
            id: 'settings.organization.invoicing.companies.detail.editable-title.cancel',
            message: 'Cancel renaming',
          })}
          inputAriaLabel={t({
            id: 'settings.organization.invoicing.companies.detail.editable-title.input',
            message: 'Company name',
          })}
        />
      </Stack>

      <Tabs selectedKey={section || 'overview'}>
        <TabList>
          <Tab id="overview" href={`/settings_beta/billing/companies/${company.id}`}>
            <Trans id="settings.organization.invoicing.companies.detail.tab.overview">
              Overview
            </Trans>
          </Tab>
          <Tab id="entities" href={`/settings_beta/billing/companies/${company.id}/entities`}>
            <Trans id="settings.organization.invoicing.companies.detail.tab.entities">
              Entities
            </Trans>
          </Tab>
        </TabList>

        <TabPanel id={section || 'overview'}>
          <Routes>
            <Route path="" element={<OverviewSection company={company} />} />
            <Route path="entities" element={<EntitiesSection companyId={company.id} />} />
          </Routes>
        </TabPanel>
      </Tabs>
    </main>
  );
};
